import axios from 'axios';
import { ROUTES } from '@/routes';

// One-time migration: remove any leftover localStorage token
localStorage.removeItem('xfaci-auth-token');

// D-SMOKE-TRIAGE / GAP-K8-01: bounded request timeout.
//
// Without a timeout, Cloudflare-edge / Cloud-Run-cold-start hung connections
// (observed empirically on Jenkins xfaci-full/main #499 trace as Playwright
// `time: -1, status: -1` — request issued but never resolved) leave
// `apiClient.get(...)` stuck on `await` forever. In particular: the
// module-load `/auth/me` call inside `fetchAuthStatus` (D-K8-00 PR #2586,
// useAuth.ts) cannot reach its 750ms-backoff retry path because the
// original Promise never rejects. AuthGate stays in `isLoading=true` mode
// and renders `null` — producing the blank-page regression that broke all
// 22 protected-routes smoke tests under the BOTH-selector contract
// (#2585 / consultation Q1 BOTH-selector ratification 2026-05-04T22:55Z).
//
// 10s is a deliberately conservative ceiling: long enough to absorb Cloud
// Run cold-start variance (typical p95 ~3-5s on dev) without producing
// false timeouts on slow-but-healthy paths, and short enough that
// D-K8-00's transient-retry path can fire within a single user-perceptible
// wait window. axios rejects with `code === 'ECONNABORTED'` on timeout;
// `isDefinitive401(err)` returns false for that (no response object), so
// D-K8-00's catch arm correctly takes the retry path rather than flipping
// to UNAUTHENTICATED_STATE.
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true,
  timeout: 10_000,
});

// Paths that DO NOT trigger the 401 → / redirect.
//
// Rationale (D-K7-06 / GAP-K7-06): the module-level `apiClient.get('/auth/me')`
// in `useAuth.ts` fires on every page load — including unauthenticated routes
// like /login. On a fresh /login visit there are no cookies yet, so /auth/me
// returns 401. Without this guard the interceptor used to fire
// `window.location.href = '/'` which (a) caused a full page reload, (b) re-fired
// the module-level /auth/me again with the same 401, and (c) the React Router
// `<Route index>` on `/` then redirected to /upload, which AuthGate then bounced
// back to /login — producing the redirect loop documented in
// auth-redirect-regression.spec.ts (#2220 follow-up).
//
// On these public/auth routes the user is already where they need to be; a 401
// is the *expected* state, not a session-expired event. The redirect-on-401
// behaviour is only meaningful on protected routes (where the user was logged
// in and the session subsequently expired mid-session).
//
// `ROUTES.root` is included because the index route at `/` declaratively
// redirects to /upload (a protected route) inside AuthGate; if /auth/me is 401
// at that point, AuthGate already does the right thing (`<Navigate to=login>`).
// Hard-redirecting to / from an interceptor would cause a reload loop here too.
const PUBLIC_AUTH_PATHS: ReadonlySet<string> = new Set([
  ROUTES.root,
  ROUTES.login,
  ROUTES.register,
  ROUTES.forgotPassword,
  ROUTES.resetPassword,
  ROUTES.changePassword,
  ROUTES.partnerRegister,
  ROUTES.privacy,
  ROUTES.security,
]);

function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATHS.has(pathname);
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401) {
      const reason = error.response?.headers?.['x-session-expired-reason'];
      // Only redirect to /?reason=... when the user is on a *protected* route.
      // On public/auth routes a 401 is expected (no cookies yet) — redirecting
      // there causes a full-page-reload loop. See PUBLIC_AUTH_PATHS comment.
      if (!isPublicAuthPath(window.location.pathname)) {
        const params = new URLSearchParams();
        if (reason === 'idle') params.set('reason', 'idle');
        else if (reason === 'absolute') params.set('reason', 'expired');
        const query = params.toString();
        window.location.href = ROUTES.root + (query ? `?${query}` : '');
      }
    }
    return Promise.reject(error);
  },
);
